import React, { FC, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useDebounceValue } from 'usehooks-ts';
import { useTranslation } from 'react-i18next';
import * as S from './DesktopFilters.styles';
import { Property } from '@/api/types/categories.types';
import { RangeCollection } from '@/components/Filters/FiltersDrawer.types';
import { DesktopFilterAccordion } from '@/components/Filters/DesktopFilters/DesktopFilterAccordion';
import { formatRangeValue } from '@/components/Filters/DesktopFilters/DesktopFilters.helpers';
import { Input } from '@/components/UI/Input/Input';
import { EMPTY_RESULTS } from '@/components/Filters/RangeOptions.constants';
import { QUERY } from '@/constants/common';
import { KeywordsSource } from '@/components/SearchPanel/components/SearchSuggestions.types';

interface Props {
  property: Property;
  collection: RangeCollection;
}

export const RangeDesktopFilter: FC<Props> = ({ property, collection }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const [selectedFrom, selectedTo] =
    collection[property.legacyResourceId] || EMPTY_RESULTS;
  const selectedValues = {
    from: typeof selectedFrom === 'number' ? selectedFrom.toString() : '',
    to: typeof selectedTo === 'number' ? selectedTo.toString() : '',
  };

  const [values, setValues] = useState<{
    from: string;
    to: string;
  }>(selectedValues);

  const [debouncedValues] = useDebounceValue(values, 1_000);

  useEffect(() => {
    applyNewValues(debouncedValues);
  }, [debouncedValues]);

  useEffect(() => {
    if (
      !isFocusedOnInput() &&
      (selectedFrom !== values.from || selectedTo !== values.to)
    ) {
      setValues(selectedValues);
    }
  }, [selectedFrom, selectedTo]);

  function isFocusedOnInput() {
    if (typeof window === 'undefined') return false;

    const activeId = document.activeElement?.id;
    const inputsIds = [
      `${property.legacyResourceId}-input-from`,
      `${property.legacyResourceId}-input-to`,
    ];
    return activeId && inputsIds.includes(activeId);
  }

  function applyNewValues(newValues: typeof values) {
    if (
      newValues.from === selectedValues.from &&
      newValues.to === selectedValues.to
    ) {
      return;
    }
    const url = new URL(window.location.href);
    const searchParams = new URLSearchParams(url.search);
    const key = `p[${property.legacyResourceId}][]`;
    searchParams.delete(key);
    searchParams.delete(QUERY.user_id);

    if (newValues.from || newValues.to) {
      searchParams.append(key, newValues.from);
      searchParams.append(key, newValues.to);
    }
    searchParams.set(QUERY.keywords_source, KeywordsSource.Typewritten);

    url.search = searchParams.toString();
    router.push(url, undefined, { scroll: false });
  }

  function handleChange(value: string | null, fieldName: keyof typeof values) {
    setValues({ ...values, [fieldName]: value });
  }

  function handleClear() {
    setValues({ from: '', to: '' });
  }

  const formattedValue = formatRangeValue(
    { from: selectedFrom?.toString(), to: selectedTo?.toString() },
    property.measurementUnits ?? '',
    t
  );

  return (
    <DesktopFilterAccordion
      title={property.name}
      value={formattedValue}
      onClear={handleClear}
      dataTestId={`range-property-${property.legacyResourceId}`}
    >
      <S.RangeFieldsContainer>
        <Input
          id={`${property.legacyResourceId}-input-from`}
          isNumber
          autoCapitalize="none"
          autoComplete="off"
          autoCorrect="off"
          spellCheck="false"
          value={values.from?.toString() ?? ''}
          onChange={value => handleChange(value as string, 'from')}
          type="search"
          appearance="filled"
          placeholder={t('common.from')}
          label={t('common.from')}
          labelPosition="left"
          customPaddingTop={2}
          data-testid="range-from"
        />
        <Input
          id={`${property.legacyResourceId}-input-to`}
          isNumber
          autoCapitalize="none"
          autoComplete="off"
          autoCorrect="off"
          spellCheck="false"
          value={values.to?.toString() ?? ''}
          onChange={value => handleChange(value as string, 'to')}
          type="search"
          appearance="filled"
          placeholder={t('common.to')}
          label={t('common.to')}
          labelPosition="left"
          customPaddingTop={2}
          data-testid="range-to"
        />
      </S.RangeFieldsContainer>
    </DesktopFilterAccordion>
  );
};
